import React, { useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { PersonSearch as PersonSearchIcon } from '@mui/icons-material';
import { OrchestratorPerson } from '../services/my-guides-admin-api';
import { MIN_SEARCH_LENGTH, usePersonSearch } from '../hooks/usePersonSearch';
import { personInitial, personLabel } from '../utils/people';

interface PersonSearchBoxProps {
  placeholder: string;
  disabled?: boolean;
  /** Hide people who are already in the list this box adds to. */
  exclude?: string[];
  /** Right-hand action for each result (a button, a chip). */
  renderAction: (user: OrchestratorPerson, clear: () => void) => React.ReactNode;
  /** A short note under each person's email (for example "Not a Tripsby email"). */
  renderNote?: (user: OrchestratorPerson) => React.ReactNode;
}

/** Search Tripsby users by email prefix and act on a result. */
export default function PersonSearchBox({
  placeholder,
  disabled,
  exclude = [],
  renderAction,
  renderNote,
}: PersonSearchBoxProps) {
  const [query, setQuery] = useState('');
  const { data, isFetching, error, active, pending } = usePersonSearch(query, !disabled);
  const results = (data ?? []).filter((u) => !exclude.includes(u.personId));
  const tooShort = query.trim().length > 0 && query.trim().length < MIN_SEARCH_LENGTH;

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        value={query}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => setQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PersonSearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: (isFetching || pending) && active ? <CircularProgress size={16} /> : undefined,
        }}
        helperText={tooShort ? `Type at least ${MIN_SEARCH_LENGTH} characters of the email` : undefined}
      />

      {error ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          {(error as Error).message}
        </Alert>
      ) : null}

      {active && !isFetching && !pending && !error && results.length === 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          No one found with an email starting "{query.trim()}".
        </Typography>
      ) : null}

      {results.length > 0 ? (
        <Paper variant="outlined" sx={{ mt: 1 }}>
          <List dense disablePadding>
            {results.map((user) => (
              <ListItem
                key={user.personId}
                divider
                secondaryAction={renderAction(user, () => setQuery(''))}
                sx={{ pr: 16 }}
              >
                <ListItemAvatar sx={{ minWidth: 40 }}>
                  <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'primary.light' }}>
                    {personInitial(user)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={personLabel(user)}
                  secondary={
                    <>
                      {user.email}
                      {renderNote?.(user)}
                    </>
                  }
                  primaryTypographyProps={{ fontWeight: 600, variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption', component: 'div' }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      ) : null}
    </Box>
  );
}
